import { useSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import { enqueueSnackbar } from '~/utils/notifications';
import { useRef } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilCallback, useRecoilState, useRecoilValue } from 'recoil';
import { useActiveFusionTarget } from '~/hooks/target/useActiveFusionTarget';
import { useDropRegistrationVoi } from '~/hooks/registration/useDropRegistrationVoi';
import { getRegistrationUndoService } from '~/domain/ServiceContainer';
import { performRigidRegistration } from '~/lib/elastix/registration';
import {
    autoRegistrationInProgressState,
    voiBoundsState,
} from '~/state/atoms/toolsAtoms';
import { handleError } from '~/utils/errors';

/**
 * Runs an automatic rigid registration of the primary row's fused pair,
 * scoped to the VOI box when one is drawn, and writes the fitted matrix into
 * the pair's registration through its Environment, with an undo point
 * before it. The registration must be the one a drag
 * from the primary edits, on a pair whose Environment is resident.
 *
 * A run is cancelled by `cancelProcessing`, or by anything that sets
 * `autoRegistrationInProgressState` false while it runs. A result whose
 * registration is no longer the one the pair's Environment holds, or was
 * approved meanwhile, is discarded and reported: it was fitted for
 * another pair.
 */
export function useElastixRegistration() {
    const intl = useIntl();
    const primaryImage = useSelectedPrimaryImage();
    const {
        secondaryImage,
        registration: selectedRegistration,
        environment,
    } = useActiveFusionTarget();
    const voiBounds = useRecoilValue(voiBoundsState);
    const registrationUndoService = getRegistrationUndoService();

    const [isSubmitting, setIsSubmitting] = useRecoilState(
        autoRegistrationInProgressState
    );
    // A run is alive while it is the latest one started here and the
    // progress atom still says so.
    const runIdRef = useRef(0);
    const isRunAlive = useRecoilCallback(
        ({ snapshot }) =>
            (runId: number) =>
                runId === runIdRef.current &&
                snapshot
                    .getLoadable(autoRegistrationInProgressState)
                    .valueOrThrow(),
        []
    );

    // The box was this run's scope; a later run seeds its own.
    const dropRegistrationVoi = useDropRegistrationVoi();

    const startProcessing = async () => {
        if (isSubmitting) {
            return;
        }

        if (
            !primaryImage ||
            !secondaryImage ||
            !selectedRegistration ||
            !environment
        ) {
            handleError('Missing required data for registration.');
            return;
        }

        runIdRef.current += 1;
        const runId = runIdRef.current;
        setIsSubmitting(true);
        try {
            const registrationId = selectedRegistration.dicomUid;
            registrationUndoService.pushState(
                registrationId,
                selectedRegistration.transformation
            );
            const newRegistration = await performRigidRegistration(
                primaryImage,
                secondaryImage,
                selectedRegistration.transformation,
                voiBounds
            );
            if (!isRunAlive(runId)) {
                return;
            }
            if (
                environment.registration !== selectedRegistration ||
                selectedRegistration.isApproved
            ) {
                handleError(
                    'The registered pair changed while registering; the ' +
                        'result was discarded.'
                );
                return;
            }
            environment.setRegistrationMatrix(newRegistration.transformation);
            selectedRegistration.syncTransformationToDataset();
            dropRegistrationVoi();
            enqueueSnackbar(
                intl.formatMessage({ id: 'registration.auto.completed' }),
                { variant: 'success' }
            );
        } catch (error) {
            if (isRunAlive(runId)) {
                handleError(
                    error,
                    intl.formatMessage({ id: 'registration.auto.failed' })
                );
            }
        } finally {
            if (runId === runIdRef.current) {
                setIsSubmitting(false);
            }
        }
    };

    const cancelProcessing = () => {
        runIdRef.current += 1;
        setIsSubmitting(false);
    };

    const hasSelectedRegistration = !!selectedRegistration;

    return {
        startProcessing,
        cancelProcessing,
        isSubmitting,
        hasSelectedRegistration,
    };
}
